/**
 * Lightweight Markdown → safe HTML for Claude skill outputs.
 * Escapes HTML first, then applies common MD patterns (no external deps).
 */

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function inlineFormat(line) {
  return line
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
    .replace(
      /\[([^\]]+)\]\((https?:[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
}

export function markdownToHtml(markdown) {
  const src = String(markdown ?? '').replace(/\r\n/g, '\n');
  if (!src.trim()) return '';

  const parts = [];
  const lines = src.split('\n');
  let i = 0;
  let inCode = false;
  let codeBuf = [];
  let listBuf = [];
  let listType = null;

  const flushList = () => {
    if (!listBuf.length) return;
    const tag = listType === 'ol' ? 'ol' : 'ul';
    parts.push(`<${tag}>${listBuf.join('')}</${tag}>`);
    listBuf = [];
    listType = null;
  };

  const flushCode = () => {
    if (!inCode) return;
    parts.push(`<pre class="md-fence"><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`);
    codeBuf = [];
    inCode = false;
  };

  while (i < lines.length) {
    const raw = lines[i];

    if (raw.trim().startsWith('```')) {
      if (inCode) flushCode();
      else {
        flushList();
        inCode = true;
        codeBuf = [];
      }
      i += 1;
      continue;
    }

    if (inCode) {
      codeBuf.push(raw);
      i += 1;
      continue;
    }

    if (/^\s*---+\s*$/.test(raw)) {
      flushList();
      parts.push('<hr />');
      i += 1;
      continue;
    }

    const h = raw.match(/^(#{1,4})\s+(.+)$/);
    if (h) {
      flushList();
      const level = Math.min(h[1].length + 1, 5);
      parts.push(`<h${level}>${inlineFormat(escapeHtml(h[2]))}</h${level}>`);
      i += 1;
      continue;
    }

    const ul = raw.match(/^\s*[-*]\s+(.+)$/);
    if (ul) {
      if (listType && listType !== 'ul') flushList();
      listType = 'ul';
      listBuf.push(`<li>${inlineFormat(escapeHtml(ul[1]))}</li>`);
      i += 1;
      continue;
    }

    const ol = raw.match(/^\s*\d+\.\s+(.+)$/);
    if (ol) {
      if (listType && listType !== 'ol') flushList();
      listType = 'ol';
      listBuf.push(`<li>${inlineFormat(escapeHtml(ol[1]))}</li>`);
      i += 1;
      continue;
    }

    if (!raw.trim()) {
      flushList();
      i += 1;
      continue;
    }

    flushList();
    parts.push(`<p>${inlineFormat(escapeHtml(raw))}</p>`);
    i += 1;
  }

  flushCode();
  flushList();
  return parts.join('\n');
}
